import React from 'react'
import { Card, Steps, Tag, Typography, Empty, Space } from 'antd'
import { ApartmentOutlined, CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined } from '@ant-design/icons'
import { useMasterData } from '@/context/MasterDataContext'
import { resolveWorkflowChain, hasActiveWorkflow } from '@/utils/workflowResolver'
import type { ApprovalStep } from '@/types'

const { Text } = Typography

interface WorkflowChainDisplayProps {
  moduleName: string
  /** Stored steps with progress, if any. If empty/omitted, resolves from Workflow Master */
  storedChain?: ApprovalStep[]
  title?: string
  style?: React.CSSProperties
}

export const WorkflowChainDisplay: React.FC<WorkflowChainDisplayProps> = ({
  moduleName,
  storedChain,
  title,
  style,
}) => {
  const { workflows } = useMasterData()
  const isEnabled = hasActiveWorkflow(workflows, moduleName)
  const matrixChain = resolveWorkflowChain(workflows, moduleName)
  const chain = storedChain && storedChain.length > 0 ? storedChain : matrixChain

  if (!isEnabled && (!storedChain || storedChain.length === 0)) {
    return null
  }

  const currentIdx = chain.findIndex(s => s.status === 'pending')

  return (
    <Card
      style={{
        borderRadius: 10,
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        marginBottom: 20,
        border: '1px solid #E4E8F0',
        ...style,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ApartmentOutlined style={{ color: '#2563EB', fontSize: 16 }} />
          <Text strong style={{ fontSize: 15, color: '#0B1426' }}>
            {title || `${moduleName} Approval Workflow`}
          </Text>
        </div>
        <Tag color={isEnabled ? 'blue' : 'default'} style={{ borderRadius: 6, fontWeight: 600, margin: 0 }}>
          {isEnabled ? 'Workflow Active' : 'Workflow Disabled'}
        </Tag>
      </div>

      {chain.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={`No approval steps configured for ${moduleName} in Workflow Master.`}
        />
      ) : (
        <Steps
          direction="vertical"
          size="small"
          current={currentIdx === -1 ? chain.length : currentIdx}
          items={chain.map((step, idx) => {
            const isDone = step.status === 'approved'
            const isFailed = step.status === 'rejected'
            const isCurrent = currentIdx === idx

            let stepStatus: 'finish' | 'process' | 'wait' | 'error' = 'wait'
            if (isDone) stepStatus = 'finish'
            else if (isFailed) stepStatus = 'error'
            else if (isCurrent) stepStatus = 'process'

            return {
              title: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 600, color: '#0B1426' }}>{step.stepName}</span>
                  {step.action && (
                    <Tag style={{ fontSize: 10, borderRadius: 4 }}>
                      {step.action === 'approve' ? 'Approve Only' : step.action === 'reject' ? 'Reject Only' : 'Approve / Reject'}
                    </Tag>
                  )}
                </div>
              ),
              subTitle: (
                <span style={{ fontSize: 12, color: '#4A5568' }}>
                  Approver: <strong>{step.approverName || 'Assigned Approver'}</strong>
                </span>
              ),
              status: stepStatus,
              description: (
                <div style={{ marginTop: 4, fontSize: 12, color: '#8A93A6' }}>
                  {step.approvedBy && (
                    <div style={{ color: isDone ? '#059669' : '#DC2626', fontWeight: 500 }}>
                      {isDone ? '✓ Approved' : '✕ Rejected'} by {step.approvedBy}
                      {step.approvalDate && ` on ${new Date(step.approvalDate).toLocaleDateString('en-IN')}`}
                    </div>
                  )}
                  {step.comments && <div style={{ fontStyle: 'italic', marginTop: 2 }}>"{step.comments}"</div>}
                </div>
              ),
            }
          })}
        />
      )}
    </Card>
  )
}

export default WorkflowChainDisplay
